import { useCallback, useEffect, useState } from "react";
import { Boxes, RefreshCw, Search, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Badge = ({ tone = "slate", children, testid }) => {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    slate: "bg-slate-100 text-slate-600 ring-slate-200",
  };
  return <span data-testid={testid} className={`text-[10px] px-2 py-0.5 rounded-full ring-1 ${tones[tone]}`}>{children}</span>;
};

function ResultTable({ rows, cols, empty, testid }) {
  if (!rows || rows.length === 0)
    return <div className="text-[12px] text-slate-400 py-6 text-center" data-testid={`${testid}-empty`}>{empty}</div>;
  return (
    <div className="overflow-auto border border-slate-200 rounded-lg" data-testid={testid}>
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50 text-slate-500">
          <tr>{cols.map((c) => <th key={c.k} className="text-left font-medium px-2.5 py-1.5">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {cols.map((c) => <td key={c.k} className="px-2.5 py-1.5 text-slate-700">{c.render ? c.render(r) : (r[c.k] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function IngramMicroTab() {
  const [conn, setConn] = useState(null);
  const [checking, setChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);
  const [tab, setTab] = useState("orders");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/apple-services/ingram/connection"); setConn(data.connection); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load Ingram Micro status"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/apple-services/ingram/connection/check");
      setLastCheck(data);
      data.ok ? toast.success(data.message) : toast.warning(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    finally { setChecking(false); }
  };

  const configured = conn?.configured;

  return (
    <div className="max-w-4xl space-y-4" data-testid="ingram-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="ingram-status-card">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-slate-900 grid place-items-center"><Boxes className="h-4.5 w-4.5 text-white" /></div>
            <div>
              <div className="text-[15px] font-semibold text-slate-900">Ingram Micro Reseller API</div>
              <div className="text-[11px] text-slate-500">Orders &amp; Invoices · read-only</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {configured
              ? <Badge tone="emerald" testid="ingram-configured-badge">Configured</Badge>
              : conn?.credentials_set
                ? <Badge tone="amber" testid="ingram-creds-badge">Credentials OK · add customer #</Badge>
                : <Badge tone="amber" testid="ingram-notconfigured-badge">Not configured</Badge>}
            <Badge tone="slate" testid="ingram-env-badge">{conn?.env || "sandbox"} · {conn?.country_code || "IN"}</Badge>
          </div>
        </div>

        {!configured && (
          <div className="mt-4 text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2" data-testid="ingram-setup-note">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Add these backend secrets (sandbox first), then re-check:
              <code className="block mt-1 text-[11px] text-amber-900">
                INGRAM_MICRO_CLIENT_ID, INGRAM_MICRO_CLIENT_SECRET, INGRAM_MICRO_CUSTOMER_NUMBER,
                INGRAM_MICRO_COUNTRY_CODE=IN, INGRAM_MICRO_ENV=sandbox, INGRAM_MICRO_SENDER_ID, INGRAM_MICRO_APPLICATION_ID
              </code>
            </div>
          </div>
        )}

        {configured && (
          <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
            <div><div className="text-slate-400">Customer #</div><div className="text-slate-800" data-testid="ingram-customer">{conn.customer_number_masked || "—"}</div></div>
            <div><div className="text-slate-400">Base URL</div><div className="text-slate-800 break-all">{conn.base_url}</div></div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <button onClick={check} disabled={checking} data-testid="ingram-check-btn"
            className="inline-flex items-center gap-1.5 text-[12px] px-3.5 py-2 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Check connection
          </button>
        </div>

        {lastCheck && (
          <div data-testid="ingram-check-result"
            className={`mt-3 text-[12px] rounded-lg p-3 flex gap-2 border ${lastCheck.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            {lastCheck.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
            <div><span className="font-medium">{lastCheck.status}</span> — {lastCheck.message}</div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["orders", "Orders"], ["invoices", "Invoices"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`ingram-subtab-${id}`}
            className={`px-3.5 py-2 text-[12.5px] -mb-px border-b-2 ${tab === id ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{label}</button>
        ))}
      </div>

      {tab === "orders" ? <OrdersPanel disabled={!configured} /> : <InvoicesPanel disabled={!configured} />}
    </div>
  );
}

function OrdersPanel({ disabled }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/apple-services/ingram/orders", { params: q ? { customerOrderNumber: q } : {} });
      setRows(data?.orders || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Order search failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="ingram-orders-panel">
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer PO / order number (blank = recent)"
          disabled={disabled} data-testid="ingram-orders-query"
          className="flex-1 h-9 border border-slate-200 rounded-md px-3 text-[12.5px] disabled:bg-slate-50" />
        <button onClick={run} disabled={disabled || busy} data-testid="ingram-orders-search-btn"
          className="inline-flex items-center gap-1.5 text-[12px] px-3.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Search
        </button>
      </div>
      <ResultTable rows={rows} testid="ingram-orders-table"
        empty={disabled ? "Configure Ingram Micro to search orders." : "No orders — run a search."}
        cols={[
          { k: "ingramOrderNumber", label: "Order #", render: (r) => r.ingramOrderNumber || r.orderNumber || "—" },
          { k: "customerOrderNumber", label: "Customer PO" },
          { k: "orderStatus", label: "Status" },
          { k: "orderDate", label: "Date" },
        ]} />
    </div>
  );
}

function InvoicesPanel({ disabled }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/apple-services/ingram/invoices", { params: q ? { customerOrderNumber: q } : {} });
      setRows(data?.invoices || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Invoice search failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="ingram-invoices-panel">
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer PO / sales order number"
          disabled={disabled} data-testid="ingram-invoices-query"
          className="flex-1 h-9 border border-slate-200 rounded-md px-3 text-[12.5px] disabled:bg-slate-50" />
        <button onClick={run} disabled={disabled || busy} data-testid="ingram-invoices-search-btn"
          className="inline-flex items-center gap-1.5 text-[12px] px-3.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Search
        </button>
      </div>
      <ResultTable rows={rows} testid="ingram-invoices-table"
        empty={disabled ? "Configure Ingram Micro to search invoices." : "No invoices — run a search."}
        cols={[
          { k: "invoiceNumber", label: "Invoice #" },
          { k: "orderNumber", label: "Sales order" },
          { k: "customerOrderNumber", label: "Customer PO" },
          { k: "invoiceStatus", label: "Status" },
          { k: "invoiceDate", label: "Date" },
          { k: "total", label: "Total", render: (r) => (r.total != null ? `${r.currency || ""} ${r.total}` : "—") },
        ]} />
    </div>
  );
}
